import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Tables } from '@/types/database';

// ---------------------------------------------------------------------------
// Web analytics (GA4 — see migration 54 and `web-analytics-sync` Edge Function).
//
// The shape returned here intentionally mirrors what `WebAnalyticsPage.tsx`
// already renders from its mock series + SAMPLE_* constants. That way the
// page only needs to swap the data source — every panel keeps reading from
// the same fields (daily[], sources[], pages[], devices[], geo[], events[]).
//
// `range` is the user's date-range selection on the page (7d / 28d / 90d).
// We fetch the full 90 days from the DB (cheap; ~1 KB/row × ~7 tables) and
// trim client-side so the user can flip ranges without a network roundtrip.
// ---------------------------------------------------------------------------

export type WebAnalyticsRange = '7d' | '28d' | '90d';

export interface WebDailyPoint {
  day: string;
  users: number;
  newUsers: number;
  sessions: number;
  engagedSessions: number;
  conversions: number;
  avgEngagementSeconds: number;
}

export interface WebRankedRow {
  key: string;
  label: string;
  hint?: string;
  users: number;
  sessions: number;
  views?: number;
  share: number; // 0..1, computed against the daily window's user/session total
}

export interface WebAnalyticsData {
  connected: boolean;        // true if `web_sessions_daily` has at least one row in the window
  daily: WebDailyPoint[];
  totals: {
    users: number;
    newUsers: number;
    sessions: number;
    engagedSessions: number;
    conversions: number;
    engagementRate: number;
    avgEngagementSeconds: number;
  };
  sources: WebRankedRow[];   // by sessionDefaultChannelGrouping
  pages: WebRankedRow[];     // top pages, hint = page title
  devices: WebRankedRow[];   // mobile / desktop / tablet
  geo: WebRankedRow[];       // top countries
  events: WebRankedRow[];    // key events, hint = "Key event" / "Event"
}

const RANGE_DAYS: Record<WebAnalyticsRange, number> = {
  '7d': 7,
  '28d': 28,
  '90d': 90,
};

function rangeStartIso(days: number): string {
  // GA4 data goes through "yesterday" — so a 7-day window means
  // [today - 7 days, today - 1 day]. We fetch by `day >= start` and let the
  // table's natural upper bound (yesterday) close the window.
  const d = new Date();
  d.setUTCHours(0, 0, 0, 0);
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

export function useWebAnalytics(range: WebAnalyticsRange) {
  const days = RANGE_DAYS[range];
  const start = rangeStartIso(days);
  return useQuery<WebAnalyticsData>({
    queryKey: ['web-analytics', range],
    queryFn: async () => {
      // Six parallel reads — total payload ≪ 1 MB on a 90-day window even
      // for a busy storefront, and React Query keeps them cached across
      // range flips. The dashboard renders 'sample data' if all are empty.
      const [sessions, pages, referrers, devices, geo, events] = await Promise.all([
        supabase
          .from('web_sessions_daily')
          .select('day, active_users, new_users, sessions, engaged_sessions, conversions, user_engagement_seconds')
          .gte('day', start)
          .order('day', { ascending: true }),
        supabase
          .from('web_pages_daily')
          .select('day, page_path, page_title, views, active_users, sessions')
          .gte('day', start),
        supabase
          .from('web_referrers_daily')
          .select('day, channel, active_users, sessions')
          .gte('day', start),
        supabase
          .from('web_devices_daily')
          .select('day, device_category, active_users, sessions')
          .gte('day', start),
        supabase
          .from('web_geo_daily')
          .select('day, country, active_users, sessions')
          .gte('day', start),
        supabase
          .from('web_events_daily')
          .select('day, event_name, event_count, is_key_event')
          .gte('day', start),
      ]);

      const firstErr = [sessions, pages, referrers, devices, geo, events].find((r) => r.error);
      if (firstErr?.error) throw firstErr.error;

      const sessionsRows = (sessions.data ?? []) as Array<Pick<Tables<'web_sessions_daily'>,
        'day' | 'active_users' | 'new_users' | 'sessions' | 'engaged_sessions' | 'conversions' | 'user_engagement_seconds'
      >>;
      const pagesRows = (pages.data ?? []) as Array<Pick<Tables<'web_pages_daily'>,
        'day' | 'page_path' | 'page_title' | 'views' | 'active_users' | 'sessions'
      >>;
      const referrersRows = (referrers.data ?? []) as Array<Pick<Tables<'web_referrers_daily'>,
        'day' | 'channel' | 'active_users' | 'sessions'
      >>;
      const devicesRows = (devices.data ?? []) as Array<Pick<Tables<'web_devices_daily'>,
        'day' | 'device_category' | 'active_users' | 'sessions'
      >>;
      const geoRows = (geo.data ?? []) as Array<Pick<Tables<'web_geo_daily'>,
        'day' | 'country' | 'active_users' | 'sessions'
      >>;
      const eventsRows = (events.data ?? []) as Array<Pick<Tables<'web_events_daily'>,
        'day' | 'event_name' | 'event_count' | 'is_key_event'
      >>;

      const daily: WebDailyPoint[] = sessionsRows.map((r) => ({
        day: r.day,
        users: r.active_users,
        newUsers: r.new_users,
        sessions: r.sessions,
        engagedSessions: r.engaged_sessions,
        conversions: r.conversions,
        avgEngagementSeconds:
          r.sessions > 0 ? Math.round(r.user_engagement_seconds / r.sessions) : 0,
      }));

      const totalUsers = daily.reduce((s, d) => s + d.users, 0);
      const totalNewUsers = daily.reduce((s, d) => s + d.newUsers, 0);
      const totalSessions = daily.reduce((s, d) => s + d.sessions, 0);
      const totalEngaged = daily.reduce((s, d) => s + d.engagedSessions, 0);
      const totalConversions = daily.reduce((s, d) => s + d.conversions, 0);
      const totalEngagementSeconds = sessionsRows.reduce(
        (s, r) => s + (r.user_engagement_seconds ?? 0),
        0,
      );

      const totals = {
        users: totalUsers,
        newUsers: totalNewUsers,
        sessions: totalSessions,
        engagedSessions: totalEngaged,
        conversions: totalConversions,
        engagementRate: totalSessions === 0 ? 0 : totalEngaged / totalSessions,
        avgEngagementSeconds:
          totalSessions === 0 ? 0 : Math.round(totalEngagementSeconds / totalSessions),
      };

      const sources = rollup(
        referrersRows,
        (r) => r.channel,
        (r) => r.channel,
        () => undefined,
        totalUsers,
        totalSessions,
      );
      const pagesRanked = rollup(
        pagesRows,
        (r) => r.page_path,
        (r) => r.page_path,
        (r) => r.page_title ?? undefined,
        totalUsers,
        totalSessions,
        (rows) => rows.reduce((s, r) => s + (r.views ?? 0), 0),
      );
      const devicesRanked = rollup(
        devicesRows,
        (r) => r.device_category,
        (r) => formatDeviceLabel(r.device_category),
        () => undefined,
        totalUsers,
        totalSessions,
      );
      const geoRanked = rollup(
        geoRows,
        (r) => r.country,
        (r) => r.country,
        () => undefined,
        totalUsers,
        totalSessions,
      );

      // Events: we surface key (conversion-grade) events first, then the
      // long tail. The dashboard's "Conversion events" panel only shows
      // is_key_event rows; we expose all rows here and let the page filter.
      const eventsRanked = rollupEvents(eventsRows);

      const connected = daily.length > 0;
      return {
        connected,
        daily,
        totals,
        sources,
        pages: pagesRanked,
        devices: devicesRanked,
        geo: geoRanked,
        events: eventsRanked,
      };
    },
  });
}

// Groups rows by a key + aggregates active_users / sessions for the period.
// Optionally accepts a `viewsFn` to add a third metric (used for top pages).
function rollup<T extends { active_users: number; sessions: number }>(
  rows: T[],
  keyFn: (r: T) => string,
  labelFn: (r: T) => string,
  hintFn: (r: T) => string | undefined,
  totalUsers: number,
  totalSessions: number,
  viewsFn?: (rows: T[]) => number,
): WebRankedRow[] {
  const byKey = new Map<string, { rows: T[]; users: number; sessions: number }>();
  for (const r of rows) {
    const k = keyFn(r);
    const bucket = byKey.get(k) ?? { rows: [], users: 0, sessions: 0 };
    bucket.rows.push(r);
    bucket.users += r.active_users;
    bucket.sessions += r.sessions;
    byKey.set(k, bucket);
  }
  const out: WebRankedRow[] = [];
  for (const [key, bucket] of byKey) {
    const head = bucket.rows[0];
    out.push({
      key,
      label: labelFn(head),
      hint: hintFn(head),
      users: bucket.users,
      sessions: bucket.sessions,
      views: viewsFn ? viewsFn(bucket.rows) : undefined,
      share:
        totalUsers > 0
          ? bucket.users / totalUsers
          : totalSessions > 0
            ? bucket.sessions / totalSessions
            : 0,
    });
  }
  // Sort by views (pages) when available, otherwise by users desc.
  out.sort((a, b) => {
    if (a.views !== undefined && b.views !== undefined) return b.views - a.views;
    return b.users - a.users;
  });
  return out;
}

function rollupEvents(
  rows: Array<{ event_name: string; event_count: number; is_key_event: boolean }>,
): WebRankedRow[] {
  const byKey = new Map<string, { count: number; isKey: boolean }>();
  for (const r of rows) {
    const bucket = byKey.get(r.event_name) ?? { count: 0, isKey: false };
    bucket.count += r.event_count;
    bucket.isKey = bucket.isKey || r.is_key_event;
    byKey.set(r.event_name, bucket);
  }
  const total = Array.from(byKey.values()).reduce((s, b) => s + b.count, 0);
  const out: WebRankedRow[] = [];
  for (const [name, bucket] of byKey) {
    out.push({
      key: name,
      label: name,
      hint: bucket.isKey ? 'Key event' : undefined,
      users: bucket.count,    // surfaced as the "value" in the ranked list
      sessions: bucket.count,
      share: total > 0 ? bucket.count / total : 0,
    });
  }
  out.sort((a, b) => b.users - a.users);
  return out;
}

function formatDeviceLabel(category: string): string {
  if (!category) return 'Unknown';
  return category.charAt(0).toUpperCase() + category.slice(1);
}

export function useLatestForecast() {
  return useQuery({
    queryKey: ['forecasts', 'latest'],
    queryFn: async () => {
      const { data: run, error: runErr } = await supabase
        .from('forecast_runs' as never)
        .select('id, ran_at, method, horizon_days')
        .order('ran_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (runErr) throw runErr;
      const runRow = run as
        | { id: string; ran_at: string; method: string; horizon_days: number }
        | null;
      if (!runRow) return { run: null, rows: [] as Array<Record<string, unknown>> };

      const { data: rows, error: rowErr } = await supabase
        .from('forecasts' as never)
        .select('item_id, for_date, predicted_qty, lower_bound, upper_bound')
        .eq('forecast_run_id', runRow.id)
        .order('for_date', { ascending: true });
      if (rowErr) throw rowErr;
      return {
        run: runRow,
        rows: (rows ?? []) as Array<Record<string, unknown>>,
      };
    },
  });
}

export function useKpis() {
  return useQuery({
    queryKey: ['kpis'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('kpi_definitions' as never)
        .select('id, key, label, unit, target_value, direction')
        .order('label', { ascending: true });
      if (error) throw error;
      return (data ?? []) as Array<{
        id: string;
        key: string;
        label: string;
        unit: string;
        target_value: number | null;
        direction: string;
      }>;
    },
  });
}

export function useKpiSnapshots(definitionId: string | null) {
  return useQuery({
    queryKey: ['kpi-snapshots', definitionId],
    enabled: !!definitionId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('kpi_snapshots' as never)
        .select('as_of, value')
        .eq('kpi_definition_id', definitionId!)
        .order('as_of', { ascending: true });
      if (error) throw error;
      return (data ?? []) as Array<{ as_of: string; value: number }>;
    },
  });
}

export function useCompetitors() {
  return useQuery({
    queryKey: ['competitors'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('competitors' as never)
        .select('id, name, website, is_active')
        .order('name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as Array<{
        id: string;
        name: string;
        website: string | null;
        is_active: boolean;
      }>;
    },
  });
}

export function useMarketingCampaigns() {
  return useQuery({
    queryKey: ['marketing-campaigns'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('marketing_campaigns' as never)
        .select('id, name, channel, starts_on, ends_on, budget_cents, status, metrics')
        .order('starts_on', { ascending: false, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as Array<{
        id: string;
        name: string;
        channel: string;
        starts_on: string | null;
        ends_on: string | null;
        budget_cents: number | null;
        status: string;
        metrics: Record<string, unknown>;
      }>;
    },
  });
}
